import React, { useEffect, useState, useRef } from 'react';
import { optimizeAuxoUrl } from './CmsImage';

export default function Lightbox({ images = [], initialIndex = 0, isOpen, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, initialIndex]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images.length]);

  if (!isOpen || !images.length) return null;

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diffX = touchStartX.current - e.changedTouches[0].clientX;
    if (diffX > 50) handleNext();
    else if (diffX < -50) handlePrev();
    touchStartX.current = null;
  };

  const currentImg = images[currentIndex];
  const currentUrl = typeof currentImg === 'string' ? currentImg : currentImg?.url || currentImg?.src;
  const currentTitle = typeof currentImg === 'object' ? currentImg?.title || currentImg?.alt : '';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex flex-col justify-between p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between text-white/80 z-10 w-full max-w-7xl mx-auto py-2">
        <div className="text-sm font-medium tracking-wide">
          {currentIndex + 1} / {images.length}
          {currentTitle && <span className="ml-3 text-white font-semibold">{currentTitle}</span>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
          aria-label="Luk visning"
        >
          <i className="fa-solid fa-xmark text-xl" />
        </button>
      </div>

      {/* Main image container */}
      <div className="relative flex-1 flex items-center justify-center max-w-6xl mx-auto w-full overflow-hidden my-2">
        {images.length > 1 && (
          <button
            type="button"
            onClick={handlePrev}
            className="absolute left-2 sm:left-4 z-10 p-3 rounded-full bg-black/50 hover:bg-black/80 text-white border border-white/20 transition-all cursor-pointer"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-lg" />
          </button>
        )}

        <img
          src={optimizeAuxoUrl(currentUrl, 1600)}
          alt={currentTitle || 'Stort billede'}
          className="max-h-[82vh] max-w-full object-contain rounded select-none shadow-2xl"
        />

        {images.length > 1 && (
          <button
            type="button"
            onClick={handleNext}
            className="absolute right-2 sm:right-4 z-10 p-3 rounded-full bg-black/50 hover:bg-black/80 text-white border border-white/20 transition-all cursor-pointer"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-lg" />
          </button>
        )}
      </div>

      {/* Bottom thumbnails */}
      {images.length > 1 && (
        <div className="flex items-center justify-center gap-2 overflow-x-auto py-2 max-w-3xl mx-auto w-full scrollbar-none">
          {images.map((img, i) => {
            const url = typeof img === 'string' ? img : img?.url || img?.src;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setCurrentIndex(i)}
                className={`relative w-14 h-14 rounded shrink-0 overflow-hidden border-2 transition-all cursor-pointer ${
                  i === currentIndex ? 'border-[#006368] scale-105 opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
                }`}
              >
                <img
                  src={optimizeAuxoUrl(url, 120)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
